import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../hooks/useLocale';
import { splitDuration } from '../utils/ttf';

const formatInZone = (date, locale, timeZone) =>
  new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);

/**
 * Countdown to the Taipei Toy Festival order deadline, with the deadline
 * shown in Taipei time and its Mexico City equivalent. Calls onClose once
 * when it reaches zero so the page can lock ordering without a reload.
 */
const TtfCountdown = ({ deadline, onClose }) => {
  const { t } = useTranslation('ttf');
  const locale = useLocale();
  const deadlineDate = new Date(deadline);
  const [now, setNow] = useState(() => Date.now());

  const remaining = deadlineDate.getTime() - now;
  const closed = remaining <= 0;

  useEffect(() => {
    if (closed) {
      onClose?.();
      return undefined;
    }
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [closed, onClose]);

  const { days, hours, minutes, seconds } = splitDuration(remaining);
  const pad = (n) => String(n).padStart(2, '0');

  return (
    <section className="ttf-countdown" aria-live="off">
      {closed ? (
        <p className="ttf-countdown-closed">{t('countdown.closed')}</p>
      ) : (
        <>
          <p className="ttf-countdown-label">{t('countdown.label')}</p>
          <div className="ttf-countdown-units" role="timer">
            {[
              [days, t('countdown.days')],
              [pad(hours), t('countdown.hours')],
              [pad(minutes), t('countdown.minutes')],
              [pad(seconds), t('countdown.seconds')],
            ].map(([value, label]) => (
              <div key={label} className="ttf-countdown-unit">
                <span className="ttf-countdown-value">{value}</span>
                <span className="ttf-countdown-unit-label">{label}</span>
              </div>
            ))}
          </div>
        </>
      )}
      <p className="ttf-countdown-zones">
        {t('countdown.closesAtTaipei', { date: formatInZone(deadlineDate, locale, 'Asia/Taipei') })}
        <br />
        {t('countdown.closesAtMexico', { date: formatInZone(deadlineDate, locale, 'America/Mexico_City') })}
      </p>
    </section>
  );
};

export default TtfCountdown;
